import classNames from 'classnames';
import { useState } from 'react';
import { Optional } from '../../../../Common/interfaces';
import { Switch } from '../../../../components';
import { createQuestionFromConfiguration } from '../../../logbook/views/QuestionView';
import {
    Accordion,
    Card,
    Exercise,
    GhostButton,
    LabelBody,
    LabelBodyHeadline,
    LabelDisplay2,
    LabelHeader4,
    ProgressBar,
    SecondaryButton,
    Spacer,
    Textarea,
    ThoughtColumn,
    Tip,
} from '../../components';
import { ExerciseData, ExerciseViewProps } from '../../exercises';
import { IllustrationPenPaper, ThoughtsBalancing } from '../../icons';
import { ExerciseStatus } from '../../interfaces';
import {
    normalizeInput,
    parseArray,
    stringifyArray,
    useSyncedState,
    useSyncedStateForJSONArrays,
} from '../../useSyncedState';
import { Module1Exercise1Popup } from './Module1Exercise1Popup';
import { Module1Exercise1ThoughtList } from './Module1Exercise1ThoughtList';

const page2PositiveThoughtsKey = 'page2PositiveThoughts';
const page2NegativeThoughtsKey = 'page2NegativeThoughts';
const page4NotesKey = 'page4Notes';

export const exerciseData11: ExerciseData = {
    ExerciseView: Module1Exercise1,
    dataKeys: [
        page2NegativeThoughtsKey,
        page2PositiveThoughtsKey,
        page4NotesKey,
    ],
    questions: [
        createQuestionFromConfiguration({
            dataKeys: [page4NotesKey],
            title: 'Ekstra refleksion',
            description:
                'Var der ubalance hos dig? Hvad kan du gøre nu og her, for at ændre den balance?',
        }),
    ],
    haveDataForLogbook(exercise) {
        return (
            !!parseArray(exercise.Data[page2PositiveThoughtsKey]).length ||
            !!parseArray(exercise.Data[page2NegativeThoughtsKey]).length
        );
    },
    LogbookHeader: ({ exercise, className }) => (
        <div className={classNames('space-y-20', className)}>
            <Module1Exercise1ThoughtList
                items={parseArray(exercise.Data[page2PositiveThoughtsKey])}
                type="positive"
            />
            <Module1Exercise1ThoughtList
                items={parseArray(exercise.Data[page2NegativeThoughtsKey])}
                type="negative"
            />
        </div>
    ),
};

function Module1Exercise1({ exercise, onContinue }: ExerciseViewProps) {
    return (
        <Exercise title="Balancer">
            <ProgressBar current={exercise.CurrentPage} total={4} />
            <Switch
                cases={{
                    1: (
                        <Page1
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 2,
                                });
                            }}
                        />
                    ),
                    2: (
                        <Page2
                            negativeThoughts={
                                exercise.Data[page2NegativeThoughtsKey]
                            }
                            positiveThoughts={
                                exercise.Data[page2PositiveThoughtsKey]
                            }
                            onContinue={(data, isFinished) =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: isFinished ? 3 : null,
                                    data: {
                                        [page2NegativeThoughtsKey]:
                                            stringifyArray(
                                                data.negativeThoughts,
                                            ),
                                        [page2PositiveThoughtsKey]:
                                            stringifyArray(
                                                data.positiveThoughts,
                                            ),
                                    },
                                })
                            }
                        />
                    ),
                    3: (
                        <Page3
                            negativeThoughts={parseArray(
                                exercise.Data[page2NegativeThoughtsKey],
                            )}
                            positiveThoughts={parseArray(
                                exercise.Data[page2PositiveThoughtsKey],
                            )}
                            onAddNegativeThought={(text) => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: null,
                                    data: {
                                        [page2NegativeThoughtsKey]:
                                            stringifyArray(
                                                parseArray(
                                                    exercise.Data[
                                                        page2NegativeThoughtsKey
                                                    ],
                                                ).concat(text),
                                            ),
                                    },
                                });
                            }}
                            onAddPositiveThought={(text) => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: null,
                                    data: {
                                        [page2PositiveThoughtsKey]:
                                            stringifyArray(
                                                parseArray(
                                                    exercise.Data[
                                                        page2PositiveThoughtsKey
                                                    ],
                                                ).concat(text),
                                            ),
                                    },
                                });
                            }}
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 4,
                                })
                            }
                        />
                    ),
                    4: (
                        <Page4
                            text={exercise.Data[page4NotesKey] ?? ''}
                            onContinue={(text) =>
                                onContinue({
                                    status: ExerciseStatus.Completed,
                                    goToNext: false,
                                    data: {
                                        [page4NotesKey]: normalizeInput(text),
                                    },
                                })
                            }
                        />
                    ),
                }}
                value={exercise.CurrentPage}
                default={1}
            />
        </Exercise>
    );
}

type PageProps = { onContinue: () => void };
function Page1({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay2>Et spørgsmål om balance</LabelDisplay2>
            <LabelBody class="mt-10">
                Denne øvelse vil guide dig i at skabe et overblik over, hvad du
                føler dig presset af, og hvad der hjælper dig med at håndtere
                presset. Ofte et det hjælpsomt at starte med at danne sig et
                overblik, fordi overblik skaber ro.
            </LabelBody>
            <ThoughtsBalancing className="mx-auto my-30" />
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Lad mig komme i gang
            </SecondaryButton>
        </>
    );
}

const negativePlaceholders = [
    'F.eks. Jeg føler mig utilstrækkelig',
    'F.eks. Kommer jeg ind på min drømmeuddannelse?',
    'F.eks. Er jeg tynd nok til sommer?',
    'F.eks. Har jeg venner nok?',
];
const positivePlaceholders = [
    'F.eks. At tale med min ven',
    'F.eks. At droppe en aflevering',
    'F.eks. At dyrke sport',
];

type Page2Props = {
    positiveThoughts: string;
    negativeThoughts: string;
    onContinue: (
        data: {
            positiveThoughts: string[];
            negativeThoughts: string[];
        },
        isFinished: boolean,
    ) => void;
};
function Page2({ onContinue, ...props }: Page2Props) {
    const [negativeThoughts, setNegativeThoughts] = useSyncedStateForJSONArrays(
        props.negativeThoughts,
        negativePlaceholders.length,
    );
    const [positiveThoughts, setPositiveThoughts] = useSyncedStateForJSONArrays(
        props.positiveThoughts,
        positivePlaceholders.length,
    );
    const [showNegativeThoughts, setShowNegativeThoughts] = useState(false);
    const [showPositiveThoughts, setShowPositiveThoughts] = useState(false);

    return (
        <>
            <LabelHeader4>Dine krav og ressourcer</LabelHeader4>
            <LabelBody class="my-10">
                Du skal nu lave to lister. På den ene liste skal du skrive alle
                de krav ned, som du oplever i hverdagen. Det kan både være indre
                krav som dine egne forventninger til ting, du skal lykkes med,
                såsom drømmejob eller antal likes på Instagram. Eller ydre krav,
                f.eks. forventninger fra venner eller stress over penge.
            </LabelBody>
            <LabelBody>
                På den anden liste skal du notere de ressourcer du har, der
                støtter og hjælper dig med at håndtere presset. Det kan være en
                ven, der er god at snakke med, eller din evne til at koble af.
            </LabelBody>

            <Card class="mt-30">
                <Accordion
                    isOpen={showNegativeThoughts}
                    onChangeIsOpen={setShowNegativeThoughts}
                    label={
                        <LabelBodyHeadline color="text-dark-red">
                            Hvad føler du dig presset af?
                        </LabelBodyHeadline>
                    }
                >
                    {negativeThoughts.map((text, idx) => (
                        <Textarea
                            key={idx}
                            placeholder={negativePlaceholders[idx] ?? ''}
                            text={text}
                            onChangeText={(text) => {
                                const copy = negativeThoughts.slice();
                                copy[idx] = text;
                                setNegativeThoughts(copy);
                            }}
                            className={'h-auto'}
                        />
                    ))}

                    <GhostButton
                        class="my-30"
                        onClick={() =>
                            setNegativeThoughts(negativeThoughts.concat(''))
                        }
                    >
                        Tilføj mere
                    </GhostButton>
                    <Tip>
                        Du kan tænke på forskellige situationer, hvor du møder
                        krav. F.eks. på dit studie, dit arbejde og også privat -
                        for det hænger alt sammen sammen. Du kan både skrive
                        ting, der presser dig her og nu, men også længere
                        tilbage.
                    </Tip>
                    <LabelBody class="mt-30">
                        Dit svar bliver gemt i logbogen, når du fortsætter.
                    </LabelBody>
                </Accordion>
            </Card>

            <Card class="mt-20">
                <Accordion
                    isOpen={showPositiveThoughts}
                    onChangeIsOpen={setShowPositiveThoughts}
                    label={
                        <LabelBodyHeadline color="text-dark-red">
                            Hvad hjælper dig, når du er presset?
                        </LabelBodyHeadline>
                    }
                >
                    {positiveThoughts.map((text, idx) => (
                        <Textarea
                            key={idx}
                            placeholder={positivePlaceholders[idx] ?? ''}
                            text={text}
                            onChangeText={(text) => {
                                const copy = positiveThoughts.slice();
                                copy[idx] = text;
                                setPositiveThoughts(copy);
                            }}
                            className={'h-auto'}
                        />
                    ))}

                    <GhostButton
                        class="my-30"
                        onClick={() =>
                            setPositiveThoughts(positiveThoughts.concat(''))
                        }
                    >
                        Tilføj mere
                    </GhostButton>

                    <Tip>
                        Du kan tænke på, hvem der støtter dig. Det kan også være
                        din tid eller din evne til at planlægge eller bede om
                        hjælp. Du kan både skrive ting, der hjælper dig her og
                        nu, men også længere tilbage. Nogen gange kan det
                        hjælpe, at gå frem og tilbage mellem de to lister.
                    </Tip>

                    <LabelBody class="mt-30">
                        Dit svar bliver gemt i logbogen, når du fortsætter.
                    </LabelBody>
                </Accordion>
            </Card>

            <Spacer />

            <SecondaryButton
                class="mt-30"
                onClick={() =>
                    onContinue({ positiveThoughts, negativeThoughts }, true)
                }
            >
                Fortsæt øvelsen
            </SecondaryButton>
        </>
    );
}

type Page3Props = PageProps & {
    negativeThoughts: string[];
    positiveThoughts: string[];
    onAddPositiveThought: (text: string) => void;
    onAddNegativeThought: (text: string) => void;
};
function Page3({
    negativeThoughts,
    positiveThoughts,
    onAddNegativeThought,
    onAddPositiveThought,
    onContinue,
}: Page3Props) {
    const [showPopup, setShowPopup] =
        useState<Optional<'negative' | 'positive'>>();

    const emptyNegativeThoughts = negativeThoughts.length < 1;
    const emptyPositiveThoughts = positiveThoughts.length < 1;
    const emptyState = emptyNegativeThoughts && emptyPositiveThoughts;

    return (
        <>
            {showPopup && (
                <Module1Exercise1Popup
                    onClose={() => setShowPopup(null)}
                    type={showPopup}
                    onItemAdded={(text) => {
                        if (showPopup === 'negative') {
                            onAddNegativeThought(text);
                        } else {
                            onAddPositiveThought(text);
                        }
                    }}
                    items={
                        showPopup === 'negative'
                            ? negativeThoughts
                            : positiveThoughts
                    }
                />
            )}
            <LabelDisplay2>Dit overblik</LabelDisplay2>
            <LabelBody class="mt-10">
                Her kan du se forskellen mellem det der presser dig, og det der
                hjælper dig. Når man oplever pres, så er det tit fordi der er
                ubalance mellem de to.
            </LabelBody>
            <LabelBody>
                Hvad tænker du, når du ser dine søjler og balancen i mellem dem?
                Du kan for eksempel overveje hvad der skal til for at skabe mere
                balance mellem dem?
            </LabelBody>
            <LabelBody class="mb-20 mt-10">
                Du kan altid klikke på dem og tilføje mere, hvis du får lyst til
                det.
            </LabelBody>
            {emptyState && (
                <LabelBodyHeadline className="mb-20">
                    For at du kan se dit overblik, skal du udfylde hvad der
                    presser og hjælper dig i starten af øvelsen.
                </LabelBodyHeadline>
            )}
            <Spacer />
            <div className="mb-40 flex items-end justify-around">
                <ThoughtColumn
                    type="negative"
                    thoughts={negativeThoughts}
                    onClick={
                        emptyNegativeThoughts
                            ? undefined
                            : () => setShowPopup('negative')
                    }
                />
                <ThoughtColumn
                    type="positive"
                    thoughts={positiveThoughts}
                    onClick={
                        emptyPositiveThoughts
                            ? undefined
                            : () => setShowPopup('positive')
                    }
                />
            </div>
            <SecondaryButton onClick={onContinue}>
                Fortsæt øvelsen
            </SecondaryButton>
        </>
    );
}

type Page4Props = {
    text: string;
    onContinue: (text: string) => void;
};
function Page4({ text: t, onContinue }: Page4Props) {
    const [text, setText] = useSyncedState(t);
    return (
        <>
            <Card>
                <IllustrationPenPaper />
                <LabelBodyHeadline color="text-dark-red" class="mb-10 mt-20">
                    Ekstra refleksion
                </LabelBodyHeadline>
                <LabelBody>
                    Var der ubalance hos dig? Hvad kan du gøre nu og her, for at
                    ændre den balance?
                </LabelBody>
                <Textarea
                    placeholder="Skriv her..."
                    text={text}
                    onChangeText={setText}
                />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer class="mt-30" />
            <SecondaryButton onClick={() => onContinue(text)}>
                Afslut øvelsen
            </SecondaryButton>
        </>
    );
}
